

/**
 * useDeleteMiddleNode - Hook to delete nodes while preserving edge connectivity
 * When a node is deleted, this hook creates new edges connecting the incomers to outgoers,
 * maintaining the flow structure.
 *
 * @module react-flow/hooks/useDeleteMiddleNode
 */

import {
  type Node,
  type Edge,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
  useReactFlow,
} from '@xyflow/react';
import { useCallback } from 'react';

export interface UseDeleteMiddleNodeOptions {
  /** Callback after edges are reconnected */
  onEdgesReconnected?: (newEdges: Edge[]) => void;
  /** Callback when nodes are deleted (before edge reconnection) */
  onNodesDeleted?: (deletedNodes: Node[]) => void;
  /** Whether to preserve edge styling from source edges */
  preserveEdgeStyle?: boolean;
}

/**
 * useDeleteMiddleNode Hook
 *
 * Usage:
 * ```tsx
 * function MyFlow() {
 *   const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
 *   const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
 *
 *   const { onNodesDelete } = useDeleteMiddleNode();
 *
 *   return (
 *     <ReactFlow
 *       nodes={nodes}
 *       edges={edges}
 *       onNodesChange={onNodesChange}
 *       onEdgesChange={onEdgesChange}
 *       onNodesDelete={onNodesDelete}
 *     />
 *   );
 * }
 * ```
 */
export function useDeleteMiddleNode(options: UseDeleteMiddleNodeOptions = {}) {
  const { onEdgesReconnected, onNodesDeleted, preserveEdgeStyle = true } = options;
  const { getNodes, getEdges, setEdges } = useReactFlow();

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      // Notify about deleted nodes first
      onNodesDeleted?.(deleted);

      const nodes = getNodes();
      const edges = getEdges();

      // Keep track of remaining nodes as we process deletions
      let remainingNodes = [...nodes];

      const newEdges = deleted.reduce((currentEdges, deletedNode) => {
        // Get nodes that connect TO this node
        const incomers = getIncomers(deletedNode, remainingNodes, currentEdges);
        // Get nodes that this node connects TO
        const outgoers = getOutgoers(deletedNode, remainingNodes, currentEdges);
        // Get all edges connected to this node
        const connectedEdges = getConnectedEdges([deletedNode], currentEdges);

        // Filter out connected edges
        const remainingEdges = currentEdges.filter(
          (edge) => !connectedEdges.includes(edge)
        );

        // Create new edges connecting each incomer to each outgoer
        const createdEdges = incomers.flatMap(({ id: sourceId }) =>
          outgoers.map(({ id: targetId }) => {
            // Find the original edge style if preserving
            const originalEdge = preserveEdgeStyle
              ? connectedEdges.find((e) => e.source === sourceId || e.target === targetId)
              : undefined;

            return {
              id: `${sourceId}->${targetId}`,
              source: sourceId,
              target: targetId,
              ...(originalEdge && {
                type: originalEdge.type,
                style: originalEdge.style,
                markerEnd: originalEdge.markerEnd,
                markerStart: originalEdge.markerStart,
                animated: originalEdge.animated,
              }),
            } as Edge;
          })
        );

        // Remove deleted node from tracking
        remainingNodes = remainingNodes.filter((n) => n.id !== deletedNode.id);

        return [...remainingEdges, ...createdEdges];
      }, edges);

      setEdges(newEdges);
      onEdgesReconnected?.(newEdges);
    },
    [getNodes, getEdges, setEdges, onEdgesReconnected, onNodesDeleted, preserveEdgeStyle]
  );

  return {
    onNodesDelete,
  };
}

export default useDeleteMiddleNode;
